import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Video, Search, TrendingUp, Sparkles, Play, Clock, Eye } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "trending", label: "Trending 7d" },
];

const CATEGORY_CHIPS = [
  { value: "", label: "All" },
  { value: "business", label: "Business" },
  { value: "tech", label: "Tech" },
  { value: "marketing", label: "Marketing" },
  { value: "product-launch", label: "Product Launch" },
  { value: "tutorial", label: "Tutorial" },
  { value: "growth", label: "Growth" },
  { value: "creator-economy", label: "Creator Economy" },
  { value: "ai", label: "AI" },
];

/**
 * Phase 26 · Public /videos browse page.
 * Grid with filters (tag, sort) + trending strip.
 */
export default function VideosBrowse() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [trending, setTrending] = useState([]);
  const [query, setQuery] = useState("");
  const initialTag = params.get("tag") || params.get("category") || "";
  const [tag, setTag] = useState(initialTag);
  const [category, setCategory] = useState(params.get("category") || "");
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const [feedV2Enabled, setFeedV2Enabled] = useState(false);

  useEffect(() => {
    document.title = `AI Videos — Global Lead Suite`;
  }, []);

  useEffect(() => {
    // Trending strip — prefer Feed V2 endpoint (watch-time based)
    (async () => {
      try {
        const r = await fetch(`${API}/api/videos/v2/trending?days=7&limit=8`);
        if (r.ok) {
          const d = await r.json();
          setTrending(d.items || []);
          setFeedV2Enabled(true);
          return;
        }
      } catch {}
      // Fallback to Phase 26 trending
      try {
        const r = await fetch(`${API}/api/videos/trending?days=7&limit=8`);
        if (r.ok) {
          const d = await r.json();
          setTrending(d.items || []);
        }
      } catch {}
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const qs = new URLSearchParams();
        qs.set("limit", "48");
        if (category) qs.set("tag", category);
        else if (tag) qs.set("tag", tag);
        qs.set("sort", sort === "trending" ? "views" : sort);
        const r = await fetch(`${API}/api/videos?${qs.toString()}`);
        if (!r.ok) { setItems([]); setTotal(0); return; }
        const d = await r.json();
        if (cancelled) return;
        let it = d.items || [];
        if (query.trim()) {
          const q = query.trim().toLowerCase();
          it = it.filter((v) => (v.title || "").toLowerCase().includes(q));
        }
        setItems(it);
        setTotal(d.total || 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tag, category, sort, query]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (category) next.set("category", category);
    else if (tag) next.set("tag", tag);
    if (sort !== "newest") next.set("sort", sort);
    setParams(next, { replace: true });
  }, [tag, category, sort, setParams]);

  const metaDesc = useMemo(
    () => `${total} short-form AI videos — 30-60s scripts, storyboards & thumbnails. AI-generated content.`,
    [total],
  );

  return (
    <div
      className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)]"
      data-testid="videos-browse-page"
    >
      <meta name="description" content={metaDesc} />
      <div className="max-w-6xl mx-auto p-6 md:p-10 space-y-8">
        <motion.header
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-end justify-between flex-wrap gap-4"
        >
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
              <Video className="h-3 w-3 text-[var(--color-secondary)]" />
              Video Engine · V1 · AI Short-form
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              AI Videos
            </h1>
            <p className="text-xs text-text-secondary mt-1 max-w-xl">
              Short-form (30–60s) content generated by 20 virtual creators.
              Script, storyboard & gradient thumbnail — metadata-first.
            </p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="h-4 w-4 text-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="videos-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="text"
              placeholder="Search videos…"
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]"
            />
          </div>
        </motion.header>

        {/* Trending strip */}
        {trending.length > 0 && (
          <section data-testid="videos-trending-strip" className="space-y-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
              <TrendingUp className="h-3 w-3 text-neutral-400" /> Trending — last 7 days
            </div>
            <div className="grid grid-flow-col auto-cols-[minmax(220px,1fr)] gap-3 overflow-x-auto pb-3 snap-x snap-mandatory">
              {trending.map((v) => (
                <VideoCard key={v.video_id} video={v} variant="trending" />
              ))}
            </div>
          </section>
        )}

        {/* Category chips (Phase 27c) */}
        <section data-testid="videos-category-chips" className="flex flex-wrap gap-2">
          {CATEGORY_CHIPS.map((c) => (
            <button
              key={c.value || "all"}
              data-testid={`videos-category-${c.value || "all"}`}
              onClick={() => { setCategory(c.value); setTag(""); }}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                category === c.value
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                  : "border-[var(--color-border)] bg-[var(--color-card)] text-text-secondary hover:border-[var(--color-secondary)] hover:text-[var(--color-secondary)]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </section>

        {/* Filter bar */}
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-text-secondary">Tag:</span>
            <input
              data-testid="videos-tag-filter"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="ex. ai_video, creator_slug…"
              className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-xs w-56 focus:outline-none focus:border-[var(--color-primary)]"
            />
            {tag && (
              <button
                data-testid="videos-tag-clear"
                onClick={() => setTag("")}
                className="text-[10px] uppercase tracking-widest text-text-secondary hover:text-[var(--color-alert)]"
              >
                clear
              </button>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-text-secondary">
            <span>Sort:</span>
            <select
              data-testid="videos-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-xs focus:outline-none"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </section>

        {/* Grid */}
        <section>
          <div className="mb-3 text-xs text-text-secondary">
            <span data-testid="videos-grid-total">{items.length}</span> of {total} videos
            {tag ? ` in "${tag}"` : ""}
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-text-secondary text-xs">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-16 text-center text-sm text-text-secondary">
              No videos match this filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map((v) => (
                <VideoCard key={v.video_id} video={v} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function VideoCard({ video, variant }) {
  const grad = video.thumbnail_gradient || {};
  const bg = grad.start_color
    ? `linear-gradient(${grad.angle || 135}deg, ${grad.start_color}, ${grad.end_color || grad.start_color})`
    : "linear-gradient(135deg, #FFFFFF, #CCCCCC)";
  return (
    <Link
      to={`/video/${encodeURIComponent(video.video_id)}`}
      data-testid={`videos-card-${video.video_id}`}
      className="group snap-start rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden hover:border-[var(--color-primary)]/60 transition-colors block"
    >
      <div
        className="relative aspect-video overflow-hidden"
        style={{ background: bg }}
      >
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-12 w-12 rounded-full bg-white/15 backdrop-blur border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="h-5 w-5 text-white ml-0.5" fill="currentColor" />
          </div>
        </div>
        <div className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-black/40 text-white/90 border border-white/20 backdrop-blur">
          <Clock className="h-2.5 w-2.5" /> {video.duration_sec}s
        </div>
        {variant === "trending" && (
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-neutral-400/30 text-neutral-100 border border-neutral-400/40 backdrop-blur">
            <TrendingUp className="h-2.5 w-2.5" /> Trending
          </div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#161616]-500/15 text-[#CCCCCC]-300 border border-[#2A2A2A]-500/40">
            <Sparkles className="h-2.5 w-2.5" /> AI Video
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] text-text-secondary">
            <Eye className="h-3 w-3" /> {video.view_count || 0}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-[var(--color-text-main)] line-clamp-2 group-hover:text-[var(--color-secondary)] transition-colors">
          {video.title}
        </h3>
        <p className="text-[11px] text-text-secondary line-clamp-2 leading-snug">
          {video.description || (video.script || "").slice(0, 100)}
        </p>
      </div>
    </Link>
  );
}
